import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useFieldContext } from '@/lib/form-context';
import { type VariantProps } from 'class-variance-authority';
import { useState } from 'react';
import { schemaFieldVariants } from '../constants';
import { filterFieldErrors } from '../utils';

interface TextFieldProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  required?: boolean;
  fieldProp?: ReturnType<typeof useFieldContext<string | undefined>>;
}

export const TextField = ({
  label,
  placeholder,
  readOnly,
  loading,
  disabled,
  autocomplete,
  required,
  fieldProp,
  variant,
}: TextFieldProps) => {
  const resolvedVariant = variant ?? 'inline';
  const contextField = useFieldContext<string | undefined>();
  const field = fieldProp ?? contextField;
  const [isFocused, setIsFocused] = useState(false);

  const errors = field.state.meta.errors;
  const hasError = !isFocused && field.state.meta.isTouched && errors.length > 0;
  const isEmpty = !field.state.value || field.state.value === '' || field.state.value === undefined;
  const displayErrors =
    hasError && required && isEmpty
      ? [{ message: `${label} is required` }]
      : filterFieldErrors(errors);

  return (
    <Field
      orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
      data-invalid={hasError || undefined}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>
      <FieldContent>
        {loading ? (
          <Skeleton className="h-4 w-full" />
        ) : readOnly ? (
          <p>{field.state.value || '—'}</p>
        ) : (
          <Input
            id={field.name}
            value={field.state.value ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              const trimmed = field.state.value?.trim() || undefined;
              if (trimmed !== field.state.value) {
                field.handleChange(trimmed);
              }
              field.handleBlur();
            }}
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            autoComplete={autocomplete}
          />
        )}
        {hasError && <FieldError errors={displayErrors} />}
      </FieldContent>
    </Field>
  );
};
